import * as _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { usePortalTokenSettings } from '../../../../../settings/organization/portal/hooks';
import type { PortalToken } from '../../../../../../../shared/publicPortal/types';

/**
 * Returns the options for the portal tokens select. This hook returns every token in the organization.
 */
const usePortalTokens = (organizationId: number, projectGroupId?: number) => {
  const { settings, isLoading, errors } = usePortalTokenSettings(organizationId);
  const [existingTokens, setExistingTokens] = useState<PortalToken[]>([]);
  const [availableTokens, setAvailableTokens] = useState<PortalToken[]>([]);
  const settingsMap = useMemo(() => _.keyBy(settings, setting => setting.id), [settings]);
  useEffect(() => {
    if (!projectGroupId) {
      setAvailableTokens(settings || []);
    } else {
      const settingsPartition = _.partition(settings, (setting) =>
        setting.releaseGroups.some((releaseGroup) => releaseGroup.fullAccess && releaseGroup.id === projectGroupId),
      );
      setExistingTokens(settingsPartition[0]);
      setAvailableTokens(settingsPartition[1]);
    }
  }, [projectGroupId, settings]);
  const onAddToken = (tokenId: number) => {
    const token = availableTokens.find((t) => t.id === tokenId);
    if (token) {
      setExistingTokens([...existingTokens, token]);
      setAvailableTokens(availableTokens.filter((t) => t.id !== tokenId));
    }
  };
  const onRemoveToken = (tokenId: number) => {
    const token = existingTokens.find((t) => t.id === tokenId);
    if (token) {
      setAvailableTokens([...availableTokens, token]);
      setExistingTokens(existingTokens.filter((t) => t.id !== tokenId));
    }
  };
  const isTokenExpired = (tokenId: number) => {
    const token = settingsMap[tokenId];

    return token?.isExpired;
  };

  return {
    availableTokenOptions: availableTokens?.map((token) => ({
      value: token.id,
      label: token.name,
    })),
    isTokenExpired,
    existingTokens,
    onAddToken,
    onRemoveToken,
    isLoading: isLoading.fetch,
    errors,
  };
};

export default usePortalTokens;
